<template>
  <RrForm class="ren-decoration-style-inspector" layout="vertical" size="small">
    <div class="ren-decoration-style-inspector__grid">
      <RrFormItem v-for="field in sizeFields" :key="field.code">
        <template #label>
          {{ field.label }}
          <TemplateLockMark :locked="isLocked(field.code)" />
        </template>
        <RrInputNumber
          :value="numberValue(field.code)"
          :disabled="isDisabled(field.code)"
          :title="fieldTitle(field.code)"
          :min="0"
          :placeholder="field.placeholder"
          @update:value="(val) => setNumber(field.code, val)"
        />
      </RrFormItem>
    </div>

    <RrFormItem>
      <template #label>
        组件外边距
        <TemplateLockMark :locked="isLocked('margin')" />
      </template>
      <RrInputNumber
        :value="numberValue('margin')"
        :disabled="isDisabled('margin')"
        :title="fieldTitle('margin')"
        :min="0"
        placeholder="统一外边距"
        @update:value="(val) => setNumber('margin', val)"
      />
    </RrFormItem>
    <details class="ren-decoration-style-inspector__more">
      <summary>外边距分边</summary>
      <div class="ren-decoration-style-inspector__grid">
        <RrFormItem v-for="field in marginSideFields" :key="field.code">
          <template #label>
            {{ field.label }}
            <TemplateLockMark :locked="isLocked(field.code)" />
          </template>
          <RrInputNumber
            :value="numberValue(field.code)"
            :disabled="isDisabled(field.code)"
            :title="fieldTitle(field.code)"
            :min="0"
            @update:value="(val) => setNumber(field.code, val)"
          />
        </RrFormItem>
      </div>
    </details>

    <RrFormItem>
      <template #label>
        组件内边距
        <TemplateLockMark :locked="isLocked('padding')" />
      </template>
      <RrInputNumber
        :value="numberValue('padding')"
        :disabled="isDisabled('padding')"
        :title="fieldTitle('padding')"
        :min="0"
        placeholder="统一内边距"
        @update:value="(val) => setNumber('padding', val)"
      />
    </RrFormItem>
    <details class="ren-decoration-style-inspector__more">
      <summary>内边距分边</summary>
      <div class="ren-decoration-style-inspector__grid">
        <RrFormItem v-for="field in paddingSideFields" :key="field.code">
          <template #label>
            {{ field.label }}
            <TemplateLockMark :locked="isLocked(field.code)" />
          </template>
          <RrInputNumber
            :value="numberValue(field.code)"
            :disabled="isDisabled(field.code)"
            :title="fieldTitle(field.code)"
            :min="0"
            @update:value="(val) => setNumber(field.code, val)"
          />
        </RrFormItem>
      </div>
    </details>

    <RrFormItem>
      <template #label>
        组件圆角
        <TemplateLockMark :locked="isLocked('radius')" />
      </template>
      <RrInputNumber
        :value="numberValue('radius')"
        :disabled="isDisabled('radius')"
        :title="fieldTitle('radius')"
        :min="0"
        :placeholder="String(WIDGET_STYLE_FALLBACKS.radius)"
        @update:value="(val) => setNumber('radius', val)"
      />
    </RrFormItem>

    <template v-for="field in extraFields" :key="field.code">
      <RrFormItem v-if="field.options?.length">
        <template #label>
          {{ field.label }}
          <TemplateLockMark :locked="isLocked(field.code)" />
        </template>
        <RrSelect
          :disabled="isDisabled(field.code)"
          :title="fieldTitle(field.code)"
          :value="String(styleValue(field.code) ?? '')"
          :options="toSelectOptions(field.options)"
          @update:value="(val) => setOption(field, String(val))"
        />
      </RrFormItem>
      <RrFormItem v-else-if="field.type === 'text' || field.type === 'list'">
        <template #label>
          {{ field.label }}
          <TemplateLockMark :locked="isLocked(field.code)" />
        </template>
        <RrInput
          type="textarea"
          :disabled="isDisabled(field.code)"
          :title="fieldTitle(field.code)"
          :value="styleText(field.code)"
          @update:value="(val) => setText(field.code, field.type, String(val))"
        />
      </RrFormItem>
      <RrFormItem v-else-if="field.type === 'color'">
        <template #label>
          {{ field.label }}
          <TemplateLockMark :locked="isLocked(field.code)" />
        </template>
        <RrColorPicker
          :disabled="isDisabled(field.code)"
          :title="fieldTitle(field.code)"
          :model-value="String(styleValue(field.code) ?? '')"
          allow-clear
          block
          @update:model-value="(val) => setInput(field.code, field.type, String(val ?? ''))"
        />
      </RrFormItem>
      <RrFormItem v-else-if="field.type === 'number'">
        <template #label>
          {{ field.label }}
          <TemplateLockMark :locked="isLocked(field.code)" />
        </template>
        <RrInputNumber
          :disabled="isDisabled(field.code)"
          :title="fieldTitle(field.code)"
          :value="numberValue(field.code)"
          @update:value="(val) => setNumber(field.code, val)"
        />
      </RrFormItem>
      <RrFormItem v-else>
        <template #label>
          {{ field.label }}
          <TemplateLockMark :locked="isLocked(field.code)" />
        </template>
        <RrInput
          :disabled="isDisabled(field.code)"
          :title="fieldTitle(field.code)"
          :value="String(styleValue(field.code) ?? '')"
          @update:value="(val) => setInput(field.code, field.type, String(val))"
        />
      </RrFormItem>
    </template>
  </RrForm>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { RrColorPicker, RrForm, RrFormItem, RrInput, RrInputNumber, RrSelect, type SelectOption } from '@xjlv2/ui'
import type { RenDecorationComponent, RenDecorationFieldDefinition } from '../../types'
import { WIDGET_STYLE_FALLBACKS } from '../widgets/widget-style-defaults'
import TemplateLockMark from '../utils/TemplateLockMark'

const BOX_CODES = new Set([
  'width',
  'height',
  'margin',
  'marginTop',
  'marginRight',
  'marginBottom',
  'marginLeft',
  'padding',
  'paddingTop',
  'paddingRight',
  'paddingBottom',
  'paddingLeft',
  'radius',
])

const sizeFields = [
  { code: 'width', label: '宽度', placeholder: '自适应' },
  { code: 'height', label: '高度', placeholder: '自适应' },
]

const marginSideFields = [
  { code: 'marginTop', label: '上外边距' },
  { code: 'marginLeft', label: '左外边距' },
  { code: 'marginRight', label: '右外边距' },
  { code: 'marginBottom', label: '下外边距' },
]

const paddingSideFields = [
  { code: 'paddingTop', label: '上内边距' },
  { code: 'paddingLeft', label: '左内边距' },
  { code: 'paddingRight', label: '右内边距' },
  { code: 'paddingBottom', label: '下内边距' },
]

const props = withDefaults(
  defineProps<{
    component: RenDecorationComponent
    fields?: RenDecorationFieldDefinition[]
    readonly?: boolean
    templateBound?: boolean
    canEditStyle?: (code: string) => boolean
  }>(),
  {
    fields: () => [],
    readonly: false,
    templateBound: false,
    canEditStyle: () => true,
  },
)

const emit = defineEmits<{
  change: [code: string, value: unknown]
}>()

const extraFields = computed(() => props.fields.filter((field) => !BOX_CODES.has(field.code)))

function styleValue(code: string): unknown {
  return props.component.style?.[code]
}

function numberValue(code: string, fallback?: number): number | null {
  const value = styleValue(code)
  if (value == null || value === '') return fallback ?? null
  const parsed = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(parsed) ? parsed : (fallback ?? null)
}

function styleText(code: string): string {
  const value = styleValue(code)
  return typeof value === 'string' ? value : JSON.stringify(value ?? [], null, 2)
}

function isDisabled(code: string): boolean {
  return props.readonly || !props.canEditStyle(code)
}

function isLocked(code: string): boolean {
  return props.templateBound && !props.canEditStyle(code)
}

function fieldTitle(code: string): string {
  return isLocked(code) ? '该字段由业务模板锁定' : ''
}

function setNumber(code: string, value: number | null) {
  emit('change', code, value == null ? undefined : value)
}

function setInput(code: string, type: string, value: string) {
  if (!value) {
    emit('change', code, undefined)
    return
  }
  emit('change', code, type === 'number' ? Number(value) : value)
}

function setOption(field: RenDecorationFieldDefinition, value: string) {
  const option = field.options?.find((item) => String(item.value) === value)
  emit('change', field.code, option?.value ?? value)
}

function setText(code: string, type: string, value: string) {
  if (type !== 'list') {
    emit('change', code, value)
    return
  }
  try {
    emit('change', code, JSON.parse(value))
  } catch {
    emit('change', code, value)
  }
}

function toSelectOptions(options?: Array<{ label?: string; value?: unknown; disabled?: boolean }>): SelectOption[] {
  if (!options) return []
  return options.map((opt) => ({
    label: String(opt.label ?? opt.value ?? ''),
    value: (opt.value as string | number | boolean | null) ?? '',
    disabled: opt.disabled,
  }))
}
</script>

<style scoped>
.ren-decoration-style-inspector {
  display: grid;
  gap: var(--rr-space-3);
}

.ren-decoration-style-inspector__grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--rr-space-3);
}

.ren-decoration-style-inspector__more {
  border: 1px solid var(--rr-color-border);
  border-radius: var(--rr-radius-control, 6px);
  padding: var(--rr-space-2) var(--rr-space-3);
  background: var(--rr-color-surface-subtle);
}

.ren-decoration-style-inspector__more summary {
  cursor: pointer;
  font-size: var(--rr-font-size-xs, 12px);
  font-weight: 600;
  color: var(--rr-color-text-muted);
}

.ren-decoration-style-inspector__more .ren-decoration-style-inspector__grid {
  margin-top: var(--rr-space-3);
}

.ren-decoration-style-inspector :deep(.rr-form-item) {
  margin-bottom: 0;
}

.ren-decoration-style-inspector :deep(.rr-form-item__label-text) {
  display: inline-flex;
  align-items: center;
  gap: var(--rr-space-1);
}
</style>
